/**
 * Offline Status Indicator Component
 * Shows offline/online status and sync queue status
 */
import { useState } from 'react';
import { useOffline } from '@/contexts/OfflineContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { 
  Wifi, WifiOff, Cloud, CloudOff, RefreshCw, 
  CheckCircle, AlertCircle, Clock, Database
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function OfflineStatusIndicator({ className }) {
  const { 
    isOnline, 
    isInitialized, 
    isSyncing, 
    syncStats, 
    lastSyncTime,
    triggerSync 
  } = useOffline();
  
  const [isOpen, setIsOpen] = useState(false);

  if (!isInitialized) {
    return null;
  }

  const hasPendingItems = syncStats.pending > 0;
  const hasFailedItems = syncStats.failed > 0;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "relative h-9 px-2",
            !isOnline && "text-orange-500",
            hasPendingItems && isOnline && "text-blue-500",
            hasFailedItems && "text-red-500",
            className
          )}
          data-testid="offline-status-btn"
        >
          {isOnline ? (
            <Wifi className="h-4 w-4" />
          ) : (
            <WifiOff className="h-4 w-4 animate-pulse" />
          )}
          
          {/* Pending badge */}
          {hasPendingItems && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-500 text-[10px] text-white flex items-center justify-center">
              {syncStats.pending > 9 ? '9+' : syncStats.pending}
            </span>
          )}
          
          {/* Failed badge */}
          {hasFailedItems && !hasPendingItems && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center">
              !
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h4 className="font-semibold flex items-center gap-2">
              <Database className="h-4 w-4" />
              Offline Storage
            </h4>
            <Badge 
              variant={isOnline ? "default" : "destructive"}
              className={cn(
                isOnline ? "bg-green-500" : "bg-orange-500"
              )}
            >
              {isOnline ? 'Online' : 'Offline'}
            </Badge>
          </div>
          
          {/* Status message */}
          <div className={cn(
            "p-3 rounded-lg text-sm",
            isOnline ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300" 
                     : "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
          )}>
            {isOnline ? (
              <div className="flex items-center gap-2">
                <Cloud className="h-4 w-4" />
                <span>Connected to server. Data syncing automatically.</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CloudOff className="h-4 w-4" />
                <span>Working offline. Changes saved locally.</span>
              </div>
            )}
          </div>
          
          {/* Sync Statistics */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Sync Queue</p>
            
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded bg-gray-100 dark:bg-gray-800">
                <Clock className="h-4 w-4 mx-auto mb-1 text-blue-500" />
                <p className="text-lg font-bold">{syncStats.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
              <div className="p-2 rounded bg-gray-100 dark:bg-gray-800">
                <CheckCircle className="h-4 w-4 mx-auto mb-1 text-green-500" />
                <p className="text-lg font-bold">{syncStats.synced}</p>
                <p className="text-xs text-gray-500">Synced</p>
              </div>
              <div className="p-2 rounded bg-gray-100 dark:bg-gray-800">
                <AlertCircle className="h-4 w-4 mx-auto mb-1 text-red-500" />
                <p className="text-lg font-bold">{syncStats.failed}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </div>
            </div>
          </div>
          
          {/* Sync Progress */}
          {isSyncing && (
            <div className="space-y-2">
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Syncing...
              </p>
              <Progress value={undefined} className="h-1" />
            </div>
          )}
          
          {/* Last Sync Time */}
          {lastSyncTime && (
            <p className="text-xs text-gray-500">
              Last synced: {lastSyncTime.toLocaleTimeString()}
            </p>
          )}
          
          {/* Sync Button */}
          <Button
            onClick={triggerSync}
            disabled={!isOnline || isSyncing || syncStats.pending === 0}
            className="w-full"
            size="sm"
          >
            {isSyncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Now
              </>
            )}
          </Button>
          
          {/* Help text */}
          <p className="text-xs text-gray-500 text-center">
            {hasPendingItems 
              ? `${syncStats.pending} item(s) waiting to sync`
              : 'All data is synced'}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
